import type { Infer } from 'superstruct';
import {
	array,
	boolean,
	defaulted,
	number,
	object,
	optional,
	partial,
	string,
} from 'superstruct';

import { parsableInteger, userString } from '@adflow/shared/util-superstruct';

const DEFAULT_RADIUS = 25;

export function isGoogleAdsLocationsEmpty(
	locations: ViewGoogleAdsCampaignLocations
) {
	return (
		(!locations.custom_locations || locations.custom_locations.length === 0) &&
		(!locations.zips || locations.zips.length === 0) &&
		(!locations.countries || locations.countries.length === 0)
	);
}

export const TargetingGeoLocationCustomLocation = object({
	name: optional(string()),
	radius: defaulted(parsableInteger(), () => DEFAULT_RADIUS),
	address_string: string(),
	distance_unit: optional(string()),
	excluded: optional(boolean()),
});

export const AddTargetingGeoLocationCustomLocation = object({
	name: optional(string()),
	radius: defaulted(parsableInteger(), () => DEFAULT_RADIUS),
	address_string: userString('validations:Required'),
	distance_unit: optional(string()),
	excluded: defaulted(boolean(), () => false),
});

export const TargetingGeoLocationZip = object({
	key: string(),
	country_code: optional(string()),
	excluded: optional(boolean()),
});

export type TargetingGeoLocationZip = Infer<typeof TargetingGeoLocationZip>;

export const AddTargetingGeoLocationZip = object({
	key: userString('validations:Required'),
	country_code: optional(string()),
	excluded: defaulted(boolean(), () => false),
});

export const TargetingGeoLocationCountry = object({
	key: string(),
	name: optional(string()),
	excluded: optional(boolean()),
});

export type TargetingGeoLocationCountry = Infer<
	typeof TargetingGeoLocationCountry
>;

export const AddTargetingGeoLocationCountry = object({
	key: userString('validations:Required'),
	name: optional(string()),
	excluded: defaulted(boolean(), () => false),
});

export const AddGoogleAdsCampaignLocations = object({
	custom_locations: optional(array(AddTargetingGeoLocationCustomLocation)),
	zips: optional(array(AddTargetingGeoLocationZip)),
	countries: optional(array(AddTargetingGeoLocationCountry)),
});

export type AddGoogleAdsCampaignLocations = Infer<
	typeof AddGoogleAdsCampaignLocations
>;

export const AddGoogleAdsCampaignLocationTemplateDto = object({
	id: optional(parsableInteger()),
	locations: AddGoogleAdsCampaignLocations,
	avbId: string(),
});

export const ViewGoogleAdsCampaignLocations = object({
	custom_locations: optional(array(TargetingGeoLocationCustomLocation)),
	zips: optional(array(TargetingGeoLocationZip)),
	countries: optional(array(TargetingGeoLocationCountry)),
});

export type ViewGoogleAdsCampaignLocations = Infer<
	typeof ViewGoogleAdsCampaignLocations
>;

export const ViewGoogleAdsCampaignLocationTemplateDto = object({
	id: number(),
	locations: ViewGoogleAdsCampaignLocations,
	adAccountId: number(),
	adAccount: optional(
		object({
			id: number(),
			name: string(),
			avbId: string(),
		})
	),
	avbId: string(),
});

export type TargetingGeoLocationCustomLocation = Infer<
	typeof TargetingGeoLocationCustomLocation
>;

export const EditGoogleAdsCampaignLocationTemplateDto = partial(
	AddGoogleAdsCampaignLocationTemplateDto
);

export type ViewGoogleAdsCampaignLocationTemplateDto = Infer<
	typeof ViewGoogleAdsCampaignLocationTemplateDto
>;
export type AddGoogleAdsCampaignLocationTemplateDto = Infer<
	typeof AddGoogleAdsCampaignLocationTemplateDto
>;
export type EditGoogleAdsCampaignLocationTemplateDto = Infer<
	typeof EditGoogleAdsCampaignLocationTemplateDto
>;
